import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './HospitalSlots.css';
import AppIcon from '../common/AppIcon';
import { getApiBaseUrl } from '../../services/api';
import { emitDataChange, onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';

const HospitalSlots = () => {
  const [hospitals, setHospitals] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [slotAnalysis, setSlotAnalysis] = useState({});
  const [combinationSuggestions, setCombinationSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);
  const [editingHospital, setEditingHospital] = useState(null);
  const [editForm, setEditForm] = useState({ monthlySlots: '', capacity: '' });
  const [mergePrimaryId, setMergePrimaryId] = useState('');
  const [mergeSecondaryId, setMergeSecondaryId] = useState('');
  const [merging, setMerging] = useState(false);

  const apiUrl = getApiBaseUrl();

  const analyzeSlotUsage = useCallback((hospitalsData, shiftsData) => {
    const analysis = {};
    const monthStr = selectedMonth.toString().padStart(2, '0');
    const yearStr = selectedYear.toString();

    hospitalsData.forEach(hospital => {
      const hospitalShifts = shiftsData.filter(shift => 
        shift.location === hospital.code && 
        shift.shift_date && 
        shift.shift_date.includes(`${monthStr}-${yearStr}`)
      );

      const usedSlots = hospitalShifts.length;
      const totalSlots = hospital.monthlySlots || 60;
      const availableSlots = Math.max(0, totalSlots - usedSlots);
      const utilizationRate = totalSlots > 0 ? (usedSlots / totalSlots) * 100 : 0;

      analysis[hospital.code] = {
        hospital,
        usedSlots,
        totalSlots,
        availableSlots,
        utilizationRate,
        shifts: hospitalShifts
      };
    });

    setSlotAnalysis(analysis);
    generateCombinationSuggestions(analysis);
  }, [selectedMonth, selectedYear]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [hospitalsRes, shiftsRes] = await Promise.all([
        axios.get(`${apiUrl}/api/hospitals`),
        axios.get(`${apiUrl}/api/processedresult`)
      ]);

      const hospitalsData = Array.isArray(hospitalsRes.data) ? hospitalsRes.data : [];
      const shiftsData = Array.isArray(shiftsRes.data) ? shiftsRes.data : [];

      setHospitals(hospitalsData);
      setShifts(shiftsData);

      // Analyze slot usage
      analyzeSlotUsage(hospitalsData, shiftsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, selectedMonth, selectedYear, analyzeSlotUsage]);

  useEffect(() => {
    fetchData();
    return onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.SHIFTS, DATA_TYPES.HOSPITALS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        fetchData();
      }
    });
  }, [fetchData]);

  const generateCombinationSuggestions = (analysis) => {
    const suggestions = [];
    const hospitals = Object.values(analysis);

    // Find hospitals with low utilization
    const lowUtilization = hospitals.filter(h => h.utilizationRate < 30);
    const highUtilization = hospitals.filter(h => h.utilizationRate > 80);

    // Suggest combinations for low utilization hospitals (dedupe pairs)
    const seenPairs = new Set();
    lowUtilization.forEach(hospital1 => {
      lowUtilization.forEach(hospital2 => {
        if (hospital1.hospital.code >= hospital2.hospital.code) return;
        const pairKey = `${hospital1.hospital.code}|${hospital2.hospital.code}`;
        if (seenPairs.has(pairKey)) return;
        seenPairs.add(pairKey);
          const combinedSlots = hospital1.availableSlots + hospital2.availableSlots;
          const combinedCapacity = (hospital1.hospital.capacity || 0) + (hospital2.hospital.capacity || 0);
          const totalSlots = (hospital1.totalSlots || 0) + (hospital2.totalSlots || 0);
          
          suggestions.push({
            type: 'combination',
            hospitals: [hospital1.hospital, hospital2.hospital],
            combinedSlots,
            combinedCapacity,
            totalSlots,
            reason: 'Low utilization - can combine for better efficiency',
            savings: Math.max(0, totalSlots - (hospital1.usedSlots + hospital2.usedSlots))
          });
      });
    });

    // Suggest slot redistribution for high utilization
    highUtilization.forEach(hospital => {
      const nearbyHospitals = hospitals.filter(h => 
        h.hospital.code !== hospital.hospital.code && 
        h.availableSlots > 10
      );

      nearbyHospitals.forEach(nearby => {
        const redistributedSlots = Math.min(hospital.usedSlots - hospital.totalSlots, nearby.availableSlots);
        
        if (redistributedSlots > 0) {
          suggestions.push({
            type: 'redistribution',
            fromHospital: hospital.hospital,
            toHospital: nearby.hospital,
            slotsToMove: redistributedSlots,
            reason: 'High utilization - redistribute excess slots',
            benefit: `Free up ${redistributedSlots} slots at ${hospital.hospital.code}`
          });
        }
      });
    });

    setCombinationSuggestions(suggestions.slice(0, 10)); // Limit to top 10 suggestions
  };

  // Prefer the real hospital name from API/DB; fall back only if missing.
  const getHospitalDisplayName = (hospital) => {
    if (hospital && typeof hospital.name === 'string' && hospital.name.trim()) return hospital.name.trim();
    return hospital?.code || hospital?.name || 'Hospital';
  };

  const getUtilizationColor = (rate) => {
    if (rate < 30) return 'low';
    if (rate < 70) return 'medium';
    return 'high';
  };

  const getUtilizationStatus = (rate) => {
    if (rate < 30) return 'Underutilized';
    if (rate < 70) return 'Optimal';
    return 'Overutilized';
  };

  const getHospitalById = (id) => hospitals.find((h) => String(h.id || h._id) === String(id));

  const getMergePreview = () => {
    const primary = getHospitalById(mergePrimaryId);
    const secondary = getHospitalById(mergeSecondaryId);
    if (!primary || !secondary || String(mergePrimaryId) === String(mergeSecondaryId)) return null;
    const pAnalysis = slotAnalysis[primary.code];
    const sAnalysis = slotAnalysis[secondary.code];
    return {
      primary,
      secondary,
      combinedSlots: (primary.monthlySlots || 0) + (secondary.monthlySlots || 0),
      combinedCapacity: (primary.capacity || 0) + (secondary.capacity || 0),
      combinedUsed: (pAnalysis?.usedSlots || 0) + (sAnalysis?.usedSlots || 0),
      shiftsToMove: sAnalysis?.usedSlots || 0,
    };
  };

  const mergeHospitals = async (primaryId, secondaryId) => {
    if (!primaryId || !secondaryId || String(primaryId) === String(secondaryId)) {
      alert('Select two different hospitals to merge.');
      return null;
    }
    const primary = getHospitalById(primaryId);
    const secondary = getHospitalById(secondaryId);
    if (!primary || !secondary) {
      alert('Selected hospitals not found.');
      return null;
    }
    const msg = `Merge "${secondary.code}" into "${primary.code}"?\n\n` +
      `• Combined slots: ${(primary.monthlySlots || 0) + (secondary.monthlySlots || 0)}\n` +
      `• Shifts at ${secondary.code} will move to ${primary.code}\n` +
      `• ${secondary.code} will be deactivated`;
    if (!window.confirm(msg)) return null;

    setMerging(true);
    try {
      const res = await axios.post(`${apiUrl}/api/hospitals/merge`, {
        primary_id: Number(primaryId),
        secondary_id: Number(secondaryId),
        move_shifts: true,
      });
      emitDataChange(DATA_TYPES.ALL);
      await fetchData();
      setMergePrimaryId('');
      setMergeSecondaryId('');
      return res.data;
    } catch (error) {
      console.error('Error merging hospitals:', error);
      alert(error?.response?.data?.detail || 'Failed to merge hospitals. Admin login may be required.');
      return null;
    } finally {
      setMerging(false);
    }
  };

  const handleManualMerge = async () => {
    const result = await mergeHospitals(mergePrimaryId, mergeSecondaryId);
    if (result?.ok) {
      alert(
        `Merged successfully!\n` +
        `${result.merged_from} → ${result.hospital?.code}\n` +
        `Shifts moved: ${result.shifts_moved}\n` +
        `Combined slots: ${result.combined_slots}`
      );
    }
  };

  const applySuggestion = async (suggestion) => {
    try {
      setApplyingSuggestion(true);
      
      if (suggestion.type === 'combination') {
        const [keep, absorb] = suggestion.hospitals;
        const result = await mergeHospitals(keep.id || keep._id, absorb.id || absorb._id);
        if (result?.ok) {
          alert(`Merged ${result.merged_from} into ${result.hospital?.code}. ${result.shifts_moved} shift(s) moved.`);
        }
        
      } else if (suggestion.type === 'redistribution') {
        alert(
          `Slot redistribution is a planning suggestion only.\n` +
          `Move ${suggestion.slotsToMove} shift(s) from ${suggestion.fromHospital.code} to ${suggestion.toHospital.code} manually on the Schedule tab.`
        );
      }
      
    } catch (error) {
      console.error('Error applying suggestion:', error);
      alert('Failed to apply suggestion. Please try again.');
    } finally {
      setApplyingSuggestion(false);
    }
  };

  const viewSuggestionDetails = (suggestion) => {
    let details = '';
    
    if (suggestion.type === 'combination') {
      details = `
Hospital Combination Details:
- Hospitals: ${suggestion.hospitals[0].code} + ${suggestion.hospitals[1].code}
- Combined Slots: ${suggestion.combinedSlots}
- Combined Capacity: ${suggestion.combinedCapacity} beds
- Potential Savings: ${suggestion.savings} slots
- Reason: ${suggestion.reason}
      `;
    } else {
      details = `
Slot Redistribution Details:
- From: ${suggestion.fromHospital.code}
- To: ${suggestion.toHospital.code}
- Slots to Move: ${suggestion.slotsToMove}
- Benefit: ${suggestion.benefit}
- Reason: ${suggestion.reason}
      `;
    }
    
    alert(details);
  };

  const startEditing = (hospital) => {
    setEditingHospital(hospital);
    setEditForm({
      monthlySlots: hospital.monthlySlots || 60,
      capacity: hospital.capacity || 100
    });
  };

  const cancelEditing = () => {
    setEditingHospital(null);
    setEditForm({ monthlySlots: '', capacity: '' });
  };

  const saveHospital = async () => {
    try {
      setLoading(true);
      
      const updatedHospital = {
        code: editingHospital.code,
        name: editingHospital.name,
        address: editingHospital.address,
        phone: editingHospital.phone,
        monthlySlots: parseInt(editForm.monthlySlots, 10),
        capacity: parseInt(editForm.capacity, 10),
        slotDuration: editingHospital.slotDuration || 24,
      };

      const hospitalId = editingHospital.id || editingHospital._id;
      await axios.put(`${apiUrl}/api/hospitals/${hospitalId}`, updatedHospital);
      
      // Refresh data
      await fetchData();
      emitDataChange(DATA_TYPES.HOSPITALS);
      
      // Close edit form
      setEditingHospital(null);
      setEditForm({ monthlySlots: '', capacity: '' });
      
      alert('Hospital updated successfully!');
      
    } catch (error) {
      console.error('Error updating hospital:', error);
      alert('Failed to update hospital. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hospital-slots-container">
      <div className="slots-header">
        <h2>Hospital Slot Management</h2>
        <p>Analyze and optimize hospital slot utilization</p>
      </div>

      {/* Month/Year Selector */}
      <div className="month-selector">
        <div className="selector-group">
          <label>Month:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i + 1} value={i + 1}>
                {new Date(2025, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>
        <div className="selector-group">
          <label>Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {Array.from({ length: 5 }, (_, i) => (
              <option key={2025 + i} value={2025 + i}>{2025 + i}</option>
            ))}
          </select>
        </div>
        <button type="button" onClick={fetchData} className="refresh-btn" disabled={loading}>
          <AppIcon name="refresh" size={18} strokeWidth={2.25} className={loading ? 'spin' : ''} />
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Manual Merge Locations */}
      {hospitals.length >= 2 && (
        <div className="merge-locations-section">
          <h3 className="icon-with-label">
            <AppIcon name="hospitals" size={22} strokeWidth={2.25} />
            Merge Locations
          </h3>
          <p className="merge-locations-hint">
            Combine two underused hospitals into one location. Slots and capacity are added together,
            scheduled shifts move to the kept location, and the merged hospital is deactivated.
          </p>
          <div className="merge-locations-form">
            <div className="merge-select-group">
              <label htmlFor="merge-primary">Keep (primary location)</label>
              <select
                id="merge-primary"
                value={mergePrimaryId}
                onChange={(e) => setMergePrimaryId(e.target.value)}
                disabled={merging}
              >
                <option value="">Select hospital to keep</option>
                {hospitals.map((h) => (
                  <option key={h.id || h._id} value={String(h.id || h._id)}>
                    {h.code} — {getHospitalDisplayName(h)}
                  </option>
                ))}
              </select>
            </div>
            <div className="merge-arrow" aria-hidden="true">
              <AppIcon name="arrow" size={24} strokeWidth={2.5} className="app-icon--primary" />
            </div>
            <div className="merge-select-group">
              <label htmlFor="merge-secondary">Merge into primary</label>
              <select
                id="merge-secondary"
                value={mergeSecondaryId}
                onChange={(e) => setMergeSecondaryId(e.target.value)}
                disabled={merging}
              >
                <option value="">Select hospital to merge</option>
                {hospitals
                  .filter((h) => String(h.id || h._id) !== String(mergePrimaryId))
                  .map((h) => (
                    <option key={h.id || h._id} value={String(h.id || h._id)}>
                      {h.code} — {getHospitalDisplayName(h)}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {getMergePreview() && (
            <div className="merge-preview">
              <div className="hospital-pair merge-preview-pair">
                <span>{getMergePreview().secondary.code}</span>
                <span>→</span>
                <span>{getMergePreview().primary.code}</span>
              </div>
              <div className="combination-benefits">
                <p><strong>Combined Slots:</strong> {getMergePreview().combinedSlots}</p>
                <p><strong>Combined Capacity:</strong> {getMergePreview().combinedCapacity} beds</p>
                <p><strong>Shifts to move:</strong> {getMergePreview().shiftsToMove}</p>
              </div>
            </div>
          )}

          <button
            type="button"
            className="merge-btn"
            onClick={handleManualMerge}
            disabled={merging || !mergePrimaryId || !mergeSecondaryId || mergePrimaryId === mergeSecondaryId}
          >
            {merging ? 'Merging...' : 'Merge Locations'}
          </button>
        </div>
      )}

      {/* Slot Analysis */}
      <div className="slots-analysis">
        <h3 className="icon-with-label">
          <AppIcon name="slots" size={22} strokeWidth={2.25} />
          Slot Utilization Analysis
        </h3>
        <div className="analysis-grid">
          {Object.values(slotAnalysis).map((analysis) => (
            <div key={analysis.hospital.code} className={`analysis-card ${getUtilizationColor(analysis.utilizationRate)}`}>
              <div className="hospital-header">
                <div className="hospital-code">{analysis.hospital.code}</div>
                <div className={`utilization-badge ${getUtilizationColor(analysis.utilizationRate)}`}>
                  {getUtilizationStatus(analysis.utilizationRate)}
                </div>
              </div>
              <div className="hospital-name">{getHospitalDisplayName(analysis.hospital)}</div>
              
              <div className="slot-stats">
                <div className="stat-item">
                  <span className="stat-label">Used Slots:</span>
                  <span className="stat-value used">{analysis.usedSlots}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Total Slots:</span>
                  <span className="stat-value total">{analysis.totalSlots}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Available:</span>
                  <span className="stat-value available">{analysis.availableSlots}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Utilization:</span>
                  <span className="stat-value utilization">{analysis.utilizationRate.toFixed(1)}%</span>
                </div>
              </div>

              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${Math.min(analysis.utilizationRate, 100)}%` }}
                />
              </div>

              <div className="hospital-actions">
                <button 
                  className="edit-btn"
                  onClick={() => startEditing(analysis.hospital)}
                >
                  ✏️ Edit Slots
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Combination Suggestions */}
      {combinationSuggestions.length > 0 && (
        <div className="combination-suggestions">
          <h3 className="icon-with-label">
            <AppIcon name="ai" size={22} strokeWidth={2.25} />
            Optimization Suggestions
          </h3>
          <div className="suggestions-grid">
            {combinationSuggestions.map((suggestion, index) => (
              <div key={index} className="suggestion-card">
                <div className="suggestion-header">
                  <div className="suggestion-type icon-with-label">
                    <AppIcon name={suggestion.type === 'combination' ? 'hospitals' : 'refresh'} size={18} strokeWidth={2} />
                    {suggestion.type === 'combination' ? 'Hospital Combination' : 'Slot Redistribution'}
                  </div>
                  <div className="suggestion-priority">
                    {suggestion.type === 'combination' ? 'High Priority' : 'Medium Priority'}
                  </div>
                </div>

                <div className="suggestion-content">
                  {suggestion.type === 'combination' ? (
                    <div>
                      <p><strong>Combine Hospitals:</strong></p>
                      <div className="hospital-pair">
                        <span>{suggestion.hospitals[0].code} ({getHospitalDisplayName(suggestion.hospitals[0])})</span>
                        <span>+</span>
                        <span>{suggestion.hospitals[1].code} ({getHospitalDisplayName(suggestion.hospitals[1])})</span>
                      </div>
                      <div className="combination-benefits">
                        <p><strong>Combined Slots:</strong> {suggestion.totalSlots ?? suggestion.combinedSlots}</p>
                        <p><strong>Combined Capacity:</strong> {suggestion.combinedCapacity} beds</p>
                        <p><strong>Potential Savings:</strong> {suggestion.savings} slots</p>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p><strong>Redistribute Slots:</strong></p>
                      <div className="redistribution-flow">
                        <span>{suggestion.fromHospital.code} ({getHospitalDisplayName(suggestion.fromHospital)})</span>
                        <span>→</span>
                        <span>{suggestion.toHospital.code} ({getHospitalDisplayName(suggestion.toHospital)})</span>
                      </div>
                      <div className="redistribution-details">
                        <p><strong>Slots to Move:</strong> {suggestion.slotsToMove}</p>
                        <p><strong>Benefit:</strong> {suggestion.benefit}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="suggestion-reason">
                  <strong>Reason:</strong> {suggestion.reason}
                </div>

                <div className="suggestion-actions">
                  <button 
                    className="apply-btn" 
                    onClick={() => applySuggestion(suggestion)}
                    disabled={applyingSuggestion}
                  >
                    {applyingSuggestion || merging ? 'Applying...' : 'Apply Merge'}
                  </button>
                  <button 
                    className="details-btn"
                    onClick={() => viewSuggestionDetails(suggestion)}
                  >
                    View Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="summary-stats">
        <h3>📈 Summary Statistics</h3>
        <div className="stats-grid">
          <div className="summary-card">
            <div className="summary-icon icon-badge icon-badge--md">
              <AppIcon name="hospitals" size={26} strokeWidth={2.25} />
            </div>
            <div className="summary-content">
              <h4>{Object.keys(slotAnalysis).length}</h4>
              <p>Active Hospitals</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon icon-badge icon-badge--md">
              <AppIcon name="analytics" size={26} strokeWidth={2.25} />
            </div>
            <div className="summary-content">
              <h4>{Object.values(slotAnalysis).reduce((sum, a) => sum + a.usedSlots, 0)}</h4>
              <p>Total Used Slots</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">📈</div>
            <div className="summary-content">
              <h4>{Object.values(slotAnalysis).reduce((sum, a) => sum + a.totalSlots, 0)}</h4>
              <p>Total Available Slots</p>
            </div>
          </div>
          
          <div className="summary-card">
            <div className="summary-icon">⚡</div>
            <div className="summary-content">
              <h4>{combinationSuggestions.length}</h4>
              <p>Optimization Suggestions</p>
            </div>
          </div>
        </div>
      </div>

      <div className="last-updated">
        Last updated: {new Date().toLocaleString()}
      </div>

      {/* Edit Hospital Modal */}
      {editingHospital && (
        <div className="edit-modal-overlay">
          <div className="edit-modal">
            <div className="edit-modal-header">
              <h3>Edit Hospital: {editingHospital.name}</h3>
              <button className="close-btn" onClick={cancelEditing}>×</button>
            </div>
            
            <div className="edit-modal-content">
              <div className="form-group">
                <label>Monthly Slots:</label>
                <input
                  type="number"
                  value={editForm.monthlySlots}
                  onChange={(e) => setEditForm({...editForm, monthlySlots: e.target.value})}
                  min="1"
                  max="1000"
                />
              </div>
              
              <div className="form-group">
                <label>Capacity (Beds):</label>
                <input
                  type="number"
                  value={editForm.capacity}
                  onChange={(e) => setEditForm({...editForm, capacity: e.target.value})}
                  min="1"
                  max="1000"
                />
              </div>
            </div>
            
            <div className="edit-modal-actions">
              <button className="cancel-btn" onClick={cancelEditing}>
                Cancel
              </button>
              <button 
                className="save-btn" 
                onClick={saveHospital}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HospitalSlots;
